import React from 'react'
import { FormControl, IconButton, InputBase, type InputBaseProps } from '@mui/material'

import { Icon } from '~components/icons'

import './AccessibleGrid.scss'

interface CellNumberInputProps extends Omit<InputBaseProps, 'onChange'> {
  placeholder?: string
  value?: number | null
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  min?: number
  max?: number
  step?: number
}

const CellNumberInput: React.FC<CellNumberInputProps> = ({
  placeholder = '',
  onChange,
  min = 0,
  max = Infinity,
  step = 1,
  value = null,
  disabled,
  ...props
}) => {
  const inputRef = React.useRef<HTMLInputElement>(null)

  const wheelHandler = { onWheel: (e: React.WheelEvent): void => (e.target as HTMLElement).blur() }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    onChange(event)
  }

  const getCurrentValue = (): number | null => {
    if (value === null || value === undefined) return null
    if (typeof value === 'number') return value
    const stringValue = String(value)
    if (stringValue === '') return null
    const num = parseFloat(stringValue)
    return isNaN(num) ? null : num
  }

  const createSyntheticEvent = (newValue: number | null): React.ChangeEvent<HTMLInputElement> => {
    if (inputRef.current) {
      inputRef.current.value = newValue === null ? '' : newValue.toString()
      const event = new Event('change', { bubbles: true })
      Object.defineProperty(event, 'target', { value: inputRef.current })
      return event as unknown as React.ChangeEvent<HTMLInputElement>
    }
    return {
      target: { value: newValue === null ? '' : newValue.toString() },
      currentTarget: { value: newValue === null ? '' : newValue.toString() },
    } as React.ChangeEvent<HTMLInputElement>
  }

  const increment = (): void => {
    const currentValue = getCurrentValue()
    const newValue = Math.min((currentValue ?? 0) + step, max)
    onChange(createSyntheticEvent(newValue))
  }

  const decrement = (): void => {
    const currentValue = getCurrentValue()
    const newValue = Math.max((currentValue ?? 0) - step, min)
    onChange(createSyntheticEvent(newValue))
  }

  const isAtMax = (getCurrentValue() ?? 0) >= max
  const isAtMin = (getCurrentValue() ?? 0) <= min
  return (
    <FormControl className='cell-number-input'>
      <InputBase
        inputRef={inputRef}
        type='number'
        placeholder={placeholder}
        onChange={handleChange}
        value={value === null ? '' : value}
        disabled={disabled}
        className='cell-number-input-base'
        inputProps={{
          min,
          max,
          step,
        }}
        {...wheelHandler}
        {...props}
      />
      <div className='cell-number-input-chevrons'>
        <IconButton
          aria-label='Increase number'
          onClick={increment}
          disabled={disabled || isAtMax}
          size='small'
          className='cell-number-input-chevron cell-number-input-chevron--up'
          role='button'
          aria-roledescription='stepper'
        >
          <Icon name='KeyboardArrowUp' size={20} />
        </IconButton>
        <IconButton
          aria-label='Decrease number'
          onClick={decrement}
          disabled={disabled || isAtMin}
          size='small'
          className='cell-number-input-chevron cell-number-input-chevron--down'
          role='button'
          aria-roledescription='stepper'
        >
          <Icon name='KeyboardArrowDown' size={20} />
        </IconButton>
      </div>
    </FormControl>
  )
}

export default CellNumberInput
